import { queryLoginUser, simulateLogin } from '@/api/login'
import { useAuth } from '@/plugins/auth/src'
import {
  clearSimulatedLoginSession,
  isSimulatedLoginFlag,
  markSimulatedLoginSession,
  readCompanyCode,
  resolveSimulatedDashboardEntry,
  syncSimulatedLoginSession
} from '@/plugins/auth/simulated-login-session'

export {
  hasSimulatedLoginSession,
  isShareAppPath,
  isSimulatedLoginFlag,
  readCompanyCode,
  simulatedRouteDecision
} from '@/plugins/auth/simulated-login-session'

const SIMULATED_USER_FIELDS = ['simulatedLogin', 'simulatedOpenApiUserId', 'simulatedApiPermissions'] as const

function unwrapLoginUser (payload: any): any {
  if (!payload || typeof payload !== 'object') return null
  if (
    'simulatedLogin' in payload ||
    'userRole' in payload ||
    'userRoles' in payload
  ) {
    return payload
  }
  if (payload.user && typeof payload.user === 'object') return payload.user
  return payload
}

/** `GET /login/login/user` decides `simulatedLogin` and the related fields. */
export async function attachSimulatedLoginFields (user: any): Promise<any> {
  if (!user || typeof user !== 'object') return user
  try {
    const source = unwrapLoginUser(await queryLoginUser())
    if (source) {
      SIMULATED_USER_FIELDS.forEach(key => {
        if (source[key] !== undefined) user[key] = source[key]
      })
    }
  } catch {
    // Keep fields already present on queryUserInfoByUserId when this lookup fails.
  }
  syncSimulatedLoginSession(user)
  return user
}

function readToken (data: any): string {
  if (!data) return ''
  if (typeof data === 'string') return data.trim()
  const token = data.token ?? data.accessToken ?? data.access_token
  return token != null ? String(token).trim() : ''
}

/** `GET /api/air8-auth-center/login/simulate?companyCode=` and store the returned session. */
export async function loginByShareCompanyCode (companyCode: string): Promise<void> {
  const code = readCompanyCode(companyCode)
  if (!code) return
  const data = await simulateLogin(code)
  const token = readToken(data)
  if (!token) throw new Error('SIMULATE_LOGIN_NO_TOKEN')
  const { auth } = useAuth()
  auth.principle = null
  localStorage.removeItem('authentication-userid')
  localStorage.setItem('authentication', JSON.stringify({ token }))
  try {
    const source = unwrapLoginUser(await queryLoginUser())
    syncSimulatedLoginSession(source)
  } catch {
    markSimulatedLoginSession()
  }
}

export function isSimulatedLoginUser (auth: { principle?: { user?: { simulatedLogin?: unknown } } | null }): boolean {
  return isSimulatedLoginFlag(auth?.principle?.user?.simulatedLogin)
}

export function simulatedDashboardEntry (auth: { accessSync: (pid: string) => boolean }): string {
  return resolveSimulatedDashboardEntry(path => auth.accessSync(path))
}

/** Drop the simulate-login session and open the email/password login page. */
export function redirectSimulatedUserToPasswordLogin (targetPath: string) {
  const { auth } = useAuth()
  auth.principle = null
  localStorage.removeItem('authentication')
  localStorage.removeItem('authentication-userid')
  clearSimulatedLoginSession()
  const target = new URL(targetPath || '/', window.location.origin).href
  window.location.assign(`/login?backUrl=${encodeURIComponent(target)}`)
}
